import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useCallback, useContext, useState } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { customerState } from '@/app/auth/auth.state'
import { ModalDialogContext } from '@/app/common/context/modalDialogContext'
import { useLazyQuery } from '@/app/common/graphql/hooks'
import CustomDealSuccessModal from '@/blocks/CustomDealForm/CustomDealSuccessModal'
import { useCreateCustomDeal } from '@/blocks/CustomDealForm/useCreateCustomDeal'
import ContactDataModal from '@/blocks/Product/ContactDataModal'
import { buildDealCalculationVariables } from '@/domains/checkout/checkout.service'
import {
  checkoutDateState,
  dealCalculationState,
  dealDurationInDaysState,
  selectedItemsState,
} from '@/domains/checkout/checkout.state'
import * as tracking from '@/helpers/tracking'
import useCompany from '@/helpers/useCompany'
import { useSavePathBookmark } from '@/helpers/usePathBookmarks'
import useShowSnackbar from '@/helpers/useShowSnackbar'
import { SelectedItemsType } from '@/types'
import {
  CreateCustomDealMutation,
  CustomDealCreateArgs,
  DealCalculationDocument,
  DealCalculationQuery,
  DealCalculationQueryVariables,
  EAppraisalCheckoutKind,
  EDealType,
  GetItemCategoryDocument,
  GetItemCategoryQuery,
  GetItemCategoryQueryVariables,
} from '@/types/gql/graphql'
import { AppraisalRequest, AppraisalValuation } from './useAppraisalFlow'

type CheckoutPayload = NonNullable<AppraisalValuation['checkout']>

const mapAnswers = (answers: CheckoutPayload['answers']) =>
  answers.map((answer) => ({
    questionId: answer.questionId,
    selectedOptionIndex: answer.selectedOptionIndex ?? undefined,
    rangeValue: answer.rangeValue ?? undefined,
  }))

/**
 * Turns an appraisal valuation into a deal: DIRECT_CHECKOUT items
 * (KNOWN_PRODUCT, GOLD) are re-priceable by the regular dealCalculation and
 * enter the normal checkout; CUSTOM_DEAL_REQUEST items (AI-estimated) become
 * a custom deal request that staff review first.
 */
export function useAppraisalHandoff() {
  const { t } = useTranslation('wertCheck')
  const router = useRouter()
  const company = useCompany()
  const [customer] = useRecoilState(customerState)
  const modal = useContext(ModalDialogContext)
  const savePathBookmark = useSavePathBookmark()
  const setSelectedItems = useSetRecoilState(selectedItemsState)
  const setDealCalculation = useSetRecoilState(dealCalculationState)
  const setDealDurationInDays = useSetRecoilState(dealDurationInDaysState)
  const setCheckoutDate = useSetRecoilState(checkoutDateState)
  const { showError } = useShowSnackbar()
  const [isSubmitting, setSubmitting] = useState(false)

  const [getItemCategory] = useLazyQuery<
    GetItemCategoryQuery,
    GetItemCategoryQueryVariables
  >(GetItemCategoryDocument)

  const [getDealCalculation] = useLazyQuery<
    DealCalculationQuery,
    DealCalculationQueryVariables
  >(DealCalculationDocument)

  const { createCustomDeal } = useCreateCustomDeal()

  const startDirectCheckout = useCallback(
    async (valuation: AppraisalValuation, payload: CheckoutPayload) => {
      if (!company) return

      const categoryResult = await getItemCategory({
        variables: { itemCategoryId: payload.itemCategoryId },
      })
      const itemCategory = categoryResult.data?.getItemCategory
      if (!itemCategory) {
        showError(t('handoff_failed'))
        return
      }

      const item: SelectedItemsType = {
        title: valuation.productName,
        itemCategoryId: payload.itemCategoryId,
        itemCategory,
        answers: mapAnswers(payload.answers),
        // 'custom' routes the calculator into the existing-values branch
        // without dereferencing a catalogue product
        algoliaReference: payload.algoliaReference ?? 'custom',
        material: payload.material ?? undefined,
        isInvestmentGold: payload.isInvestmentGold ?? undefined,
        useOldValuesOfPrediction: payload.useOldValuesOfPrediction ?? undefined,
        existingCurrentMarketValue:
          payload.existingCurrentMarketValue ?? undefined,
        existingPredictedMarketValue:
          payload.existingPredictedMarketValue ?? undefined,
      }

      const result = await getDealCalculation({
        variables: buildDealCalculationVariables({
          companyId: company._id,
          dealType: EDealType.Pawn,
          items: [item],
          durationInDays: payload.durationInDays,
        }),
        onError: () => showError(t('handoff_failed')),
      })
      if (!result.data?.dealCalculation) return

      const { dealValuesEntry, itemsValuesEntries } =
        result.data.dealCalculation
      setSelectedItems([item])
      setDealDurationInDays(dealValuesEntry.durationInDays)
      setDealCalculation(result.data.dealCalculation)
      setCheckoutDate(new Date())

      tracking.addToCartAppraisal({
        productName: valuation.productName,
        categoryNames:
          itemCategory.parentCategories?.map((category) => category.name) ?? [],
        value:
          itemsValuesEntries[itemsValuesEntries.length - 1]?.loanToValue ??
          valuation.payoutAmount,
      })

      savePathBookmark()
      router.push('/checkout/items')
    },
    [
      company,
      getDealCalculation,
      getItemCategory,
      router,
      savePathBookmark,
      setCheckoutDate,
      setDealCalculation,
      setDealDurationInDays,
      setSelectedItems,
      showError,
      t,
    ],
  )

  /** Creates the request (after collecting contact data from guests). */
  const submitCustomDealRequest = useCallback(
    async (customDealCreateArgs: CustomDealCreateArgs, value: number) => {
      const handleSuccess = (
        customDeal: CreateCustomDealMutation['createCustomDeal'],
      ) => {
        tracking.trackEvent('custom_deal_request', {
          transaction_id: customDeal._id,
          value,
          type: 'pawn',
        })
      }

      if (!customer) {
        modal.open(
          <ContactDataModal
            args={customDealCreateArgs}
            onSuccess={handleSuccess}
          />,
          { variant: 'full' },
        )
        return
      }

      const result = await createCustomDeal({
        variables: { customDealCreateArgs },
      })
      if (!result.data) return

      try {
        handleSuccess(result.data.createCustomDeal)
        tracking.trackDealBooked({
          dealId: result.data.createCustomDeal._id,
          type: 'pawn',
          items: result.data.createCustomDeal.items.map((item) => ({
            categoryHierarchy: item.itemCategory.parentCategories.map(
              (category) => category._id,
            ),
          })),
        })
      } finally {
        modal.open(<CustomDealSuccessModal />, {
          variant: 'full',
          onAfterClose: () => {
            router.push('/')
          },
        })
      }
    },
    [createCustomDeal, customer, modal, router],
  )

  const startCustomDealRequest = useCallback(
    async (valuation: AppraisalValuation, payload: CheckoutPayload) => {
      if (!company) return

      const noteParts = [
        valuation.marketValue != null
          ? `Marktwert ca. ${formatEur(valuation.marketValue)}`
          : null,
        `Auszahlungsschätzung ${formatEur(valuation.payoutAmount)}`,
        `Pfad ${valuation.path}${valuation.isAiGenerated ? ', KI-generiert' : ''}`,
      ].filter(Boolean)

      await submitCustomDealRequest(
        {
          companyId: company._id,
          contactData: {},
          customerId: customer?._id,
          dealType: EDealType.Pawn,
          durationInDays: company.configuration.minimumPawnDuration,
          isItemEvaluationMissing: true,
          items: [
            {
              itemCategoryId: payload.itemCategoryId,
              title: valuation.productName,
              answers: mapAnswers(payload.answers),
              // staff evaluate the item — the AI estimate is context, not an ask
              pawnPayoutAmount: 0,
              purchasePayoutAmount: 0,
              note: `KI Wert-Check: ${noteParts.join(', ')}`,
            },
          ],
        },
        valuation.payoutAmount,
      )
    },
    [company, customer, submitCustomDealRequest],
  )

  /**
   * No online estimate: the customer's own ask becomes the request's payout
   * amount, exactly as in the request form (staff answer with an offer).
   */
  const startRequest = useCallback(
    async (request: AppraisalRequest, amount: number) => {
      if (!company || isSubmitting) return

      tracking.trackEvent('appraisal_cta_click', {
        appraisal_path: request.path,
        target: 'custom_deal',
      })

      setSubmitting(true)
      try {
        await submitCustomDealRequest(
          {
            companyId: company._id,
            contactData: {},
            customerId: customer?._id,
            dealType: EDealType.Pawn,
            durationInDays: company.configuration.minimumPawnDuration,
            items: [
              {
                itemCategoryId: request.itemCategoryId,
                title: request.productName,
                answers: mapAnswers(request.answers),
                pawnPayoutAmount: amount,
                purchasePayoutAmount: amount,
                note: `KI Wert-Check: keine Online-Schätzung möglich, Wunschbetrag ${formatEur(amount)}`,
              },
            ],
          },
          amount,
        )
      } finally {
        setSubmitting(false)
      }
    },
    [company, customer, isSubmitting, submitCustomDealRequest],
  )

  const startHandoff = useCallback(
    async (valuation: AppraisalValuation) => {
      const payload = valuation.checkout
      if (!payload || isSubmitting) return

      tracking.trackEvent('appraisal_cta_click', {
        appraisal_path: valuation.path,
        target:
          payload.kind === EAppraisalCheckoutKind.DirectCheckout
            ? 'checkout'
            : 'custom_deal',
      })

      setSubmitting(true)
      try {
        if (payload.kind === EAppraisalCheckoutKind.DirectCheckout) {
          await startDirectCheckout(valuation, payload)
        } else {
          await startCustomDealRequest(valuation, payload)
        }
      } finally {
        setSubmitting(false)
      }
    },
    [isSubmitting, startCustomDealRequest, startDirectCheckout],
  )

  return { startHandoff, startRequest, isSubmitting }
}

const formatEur = (value: number) =>
  `€${Math.round(value).toLocaleString('de-AT')}`
